import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Autocomplete, Stack, Tag } from "@shopify/polaris";
import { GET_ETSY_METRIC_STORES } from "../../graphql/queries/etsy-metric";

export default function EtsyMetricStoreFilter({ selected, onChange }) {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [loadMoreError, setLoadMoreError] = useState(null);
  useEffect(() => {
    setLoadMoreError(null);
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const { data, loading, error, fetchMore } = useQuery(GET_ETSY_METRIC_STORES, {
    variables: {
      filter: {
        platform: "etsy",
        hasEtsyMetrics: true,
        search,
        limit: 200,
        offset: 0,
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const nodes = data && data.stores ? data.stores.nodes : [];
  const options = nodes.map(({ id, title }) => ({
    value: id,
    label: title || id,
  }));
  const hasMore = !!(data && data.stores && nodes.length < data.stores.total);

  return (
    <Stack vertical spacing="tight">
      <Autocomplete
        allowMultiple
        options={options}
        selected={selected.map((store) => store.value)}
        loading={loading}
        willLoadMoreResults={hasMore}
        onLoadMoreResults={() => {
          if (loading || !hasMore) return;
          setLoadMoreError(null);
          fetchMore({
            variables: {
              filter: {
                platform: "etsy",
                hasEtsyMetrics: true,
                search,
                limit: 200,
                offset: nodes.length,
              },
            },
            updateQuery: (previous, { fetchMoreResult }) =>
              fetchMoreResult
                ? {
                    ...fetchMoreResult,
                    stores: {
                      ...fetchMoreResult.stores,
                      nodes: [
                        ...previous.stores.nodes,
                        ...fetchMoreResult.stores.nodes,
                      ],
                    },
                  }
                : previous,
          }).catch(setLoadMoreError);
        }}
        onSelect={(ids) =>
          onChange(
            ids
              .map(
                (id) =>
                  selected.find((store) => store.value === id) ||
                  options.find((store) => store.value === id)
              )
              .filter(Boolean)
          )
        }
        emptyState={<span>No Etsy stores with metrics found.</span>}
        textField={
          <Autocomplete.TextField
            label="Etsy stores"
            placeholder="Search Etsy stores"
            value={input}
            onChange={setInput}
            clearButton
            onClearButtonClick={() => setInput("")}
            error={
              error
                ? error.message
                : loadMoreError
                ? loadMoreError.message
                : undefined
            }
            helpText="Only stores with Etsy metrics are listed. Leave unselected to show all of them."
          />
        }
      />
      <Stack spacing="tight">
        {selected.map((store) => (
          <Tag
            key={store.value}
            onRemove={() =>
              onChange(selected.filter((item) => item.value !== store.value))
            }
          >
            {store.label}
          </Tag>
        ))}
      </Stack>
    </Stack>
  );
}
